var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext } from "react";
import { Alert, Box } from "@mui/material";
import { useGetList } from "react-admin";
import { useTranslation } from "react-i18next";
import { effectiveTimeZone } from "@sequentech/ui-core";
import { AuthContext } from "@/providers/AuthContextProvider";
import { SettingsContext } from "@/providers/SettingsContextProvider";
import { IPermissions } from "@/types/keycloak";
import { logDetails, logMessage } from "@/components/logs/logMessage";
import { useTimeZoneService } from "@/components/timezones/timeZoneService";
import { hasOffset } from "@/components/timezones/ZonedDateTime";
import { ACTIVE_SCHEDULED_EVENTS } from "@/services/ScheduledEvent";
import { EventProcessors } from "../ScheduledEvents/CreateScheduledEvent";
import { useLogsPermissions } from "./useLogsPermissions";
/** Only a genuine transition starts the current period; repeated starts do not. */
function lockdownStarted(logs) {
    for (var _i = 0, logs_1 = logs; _i < logs_1.length; _i++) {
        var log = logs_1[_i];
        var details = logDetails(logMessage(log));
        if ((details === null || details === void 0 ? void 0 : details.locked_down) !== true)
            return null;
        if (details.previous === false) {
            var seconds = log.statement_timestamp;
            if (typeof seconds !== "number" || !Number.isFinite(seconds))
                return null;
            var date = new Date(seconds * 1000);
            return Number.isNaN(date.getTime()) ? null : date;
        }
    }
    return null;
}
/** Mounted only while the event's authoritative state is locked down. */
export var LockdownBanner = function (_a) {
    var event = _a.event;
    var t = useTranslation().t;
    var auth = useContext(AuthContext);
    var globalSettings = useContext(SettingsContext).globalSettings;
    var canReadLogs = useLogsPermissions().canReadLogs;
    var time = useTimeZoneService();
    var zone = effectiveTimeZone(event.presentation);
    var format = function (date) { return time.formatDateTimeZone(date, zone, time.text); };
    var canReadSchedule = auth.isAuthorized(true, auth.tenantId, [
        IPermissions.ADMIN_USER,
        IPermissions.SCHEDULED_EVENT_CREATE,
        IPermissions.SCHEDULED_EVENT_WRITE,
        IPermissions.SCHEDULED_EVENT_DELETE,
    ]);
    var logs = useGetList("electoral_log", {
        filter: { election_event_id: event.id, statement_kind: "LockdownChanged" },
        pagination: { page: 1, perPage: 100 },
        sort: { field: "id", order: "DESC" },
    }, { enabled: canReadLogs, refetchInterval: globalSettings.QUERY_POLL_INTERVAL_MS }).data;
    var _b = useGetList("sequent_backend_scheduled_event", {
        filter: __assign(__assign({ tenant_id: event.tenant_id, election_event_id: event.id, event_processor: EventProcessors.END_LOCKDOWN_PERIOD }, ACTIVE_SCHEDULED_EVENTS), { stopped_at: { format: "hasura-raw-query", value: { _is_null: true } } }),
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "id", order: "ASC" },
    }, { enabled: canReadSchedule, refetchInterval: globalSettings.QUERY_POLL_INTERVAL_MS }), ends = _b.data, scheduleLoaded = _b.isSuccess;
    var started = lockdownStarted(logs !== null && logs !== void 0 ? logs : []);
    var endDates = (ends !== null && ends !== void 0 ? ends : [])
        .map(function (row) { var _a; return (_a = row.cron_config) === null || _a === void 0 ? void 0 : _a.scheduled_date; })
        .filter(function (date) { return typeof date === "string" && hasOffset(date); })
        .map(function (date) { return new Date(date); })
        .filter(function (date) { return !Number.isNaN(date.getTime()); })
        .sort(function (a, b) { return a.getTime() - b.getTime(); });
    var end = endDates[0];
    var prefix = "electionEventScreen.field.lockdownState";
    return (_jsxs(Alert, { severity: "warning", icon: false, role: "status", "aria-label": t("".concat(prefix, ".policyLabel")), sx: { mx: 2, mb: 2, border: "1px solid", borderColor: "warning.light" }, children: [_jsx(Box, { component: "strong", children: started ? t("".concat(prefix, ".since"), { date: format(started) }) : t("".concat(prefix, ".banner")) }), canReadSchedule && scheduleLoaded && (_jsxs(_Fragment, { children: [" ", end
                        ? t("".concat(prefix, ".scheduledEnd"), { date: format(end) })
                        : t((ends === null || ends === void 0 ? void 0 : ends.length) ? "".concat(prefix, ".endScheduled") : "".concat(prefix, ".noScheduledEnd"))] }))] }));
};
